// The SETTINGS config for a UserUnitPriceAttribute MACHINE type, seeded on window by frank/edit.html.erb (which
// rendered it from QUOTE_BUILDER_CONFIG_SETTINGS, because the /mcc page itself is handed the SECTIONS config), and
// the UserUnitPriceAttribute tab within it -- the machine-manager list (SettingsUserUnitPriceAttributeList) is
// driven by that tab. Shared by the filter-island add/manage button and the empty-state adder, and keyed by the
// attribute key so ONE implementation serves 'printing_machine' AND 'die_cutting_machine'.
export const machineSettingsConfig = (key) => window[`frank_${key}_settings_config`] || {};

export const machineTabConfig = (key) => (
  (machineSettingsConfig(key).tabs || []).find((tab) => tab.form_type === 'UserUnitPriceAttribute')
);

// The owner's own count of this machine type (the MACHINE records, a UserUnitPriceAttribute), seeded by the page
// (so a button label is right on first paint) and kept fresh on window as machines are added, so a later read --
// or a remount -- sees the honest number. This is what the "Manage machines" button counts: an unpriced machine
// is still one to manage.
export const machineCount = (key) => Number(window[`frank_${key}_count`]) || 0;

export const setMachineCount = (key, value) => { window[`frank_${key}_count`] = value || 0; };

// The same count, BROKEN DOWN by the classifying type each machine row filters on -- printing_machine by
// printing_type, die_cutting_machine by die_cutting_material_group -- as a { type: count } map. Seeded and
// refreshed next to the total above. The section page's gate reads it so it can tell an owner who has a machine
// THIS section can use from one who only has the wrong type/group (Frank::OwnSetupCounts).
const machineCountByType = (key) => window[`frank_${key}_count_by_type`] || {};

export const setMachineCountByType = (key, value) => { window[`frank_${key}_count_by_type`] = value || {}; };

// Whether the owner has a machine of this key whose type the section ALLOWS -- allowedTypes is the row's
// allowed_types. This is what makes the section page's gate type-aware: a carton section allows a 'cartons'
// die-cutter, so a 'hard_cardboards'-only owner is still missing it. With no allowedTypes (an unconstrained caller)
// it falls back to the total, so a caller that does not care about type keeps the previous behaviour.
export const hasMachineOfType = (key, allowedTypes) => (
  allowedTypes && allowedTypes.length
    ? allowedTypes.some((type) => (Number(machineCountByType(key)[type]) || 0) > 0)
    : machineCount(key) > 0
);

// The owner's machines of this key for ONE section, counting only those for the section's part_type
// (Frank::SectionSetupRequirements.machines_by_part_type, which also checks the die-cutting machine type the listing
// filters on) and able to run the section's material thickness -- from Frank::OwnSetupCounts' machines_by_section, as
// { priced, unpricedAttributeId, materialThicknessMm, thicknessMismatchMachines }: priced when one of them has the
// owner's price, else the first unpriced one (null when there is none); the machines that are for the part_type but
// cannot run materialThicknessMm. The guided-setup wizard picks each machine step from this. A section or key it does
// not hold reads as nothing owned, so the wizard errs toward SHOWING the step.
export const sectionMachine = (sectionIndex, key) => {
  const machine = ((window.frank_machines_by_section || {})[sectionIndex] || {})[key] || {};
  return {
    priced: Boolean(machine.priced),
    unpricedAttributeId: machine.unpriced_attribute_id ?? null,
    materialThicknessMm: machine.material_thickness_mm ?? null,
    thicknessMismatchMachines: machine.thickness_mismatch_machines || [],
  };
};
export const setMachinesBySection = (value) => { window.frank_machines_by_section = value || {}; };

// Pull the owner's own setup counts fresh and re-solve the sections -- called after a setup step adds a material
// price, a machine, or a machine's price, so the page's gate and the wizard's step manager both see the new state
// and the completed step drops out. Reads the OWN-scoped /mcc own-setup-counts endpoint (never system, even for
// an admin), so it matches the page seed exactly (Frank::OwnSetupCounts) -- unlike the shared counts endpoint,
// which merges system records for admins and so would disagree with the own-only seed. It keeps the two count
// namespaces straight:
//   materials[key]               -> frank_<materialKey>_count             -- the owner's material price counts
//   machines[key].record         -> frank_<machineKey>_count              -- the machine RECORDS ("Manage" label)
//   machines[key].record_by_type -> frank_<machineKey>_count_by_type      -- records, bucketed by type (the page's gate)
//   machines_by_section          -> frank_machines_by_section             -- per section (the wizard's machine steps)
// Ends by announcing frank:own-machines-changed, which FrankQuoteBuilderPage turns into a re-solve + recompute.
export const refreshCountsAndResolve = () => (
  fetch(`/mcc/${window.user_quote_id}/own-setup-counts`, {
    cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' }
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (!data) { return; }
      Object.entries(data.materials || {}).forEach(([key, value]) => setMachineCount(key, value));
      Object.entries(data.machines || {}).forEach(([key, counts]) => {
        setMachineCount(key, counts.record);
        setMachineCountByType(key, counts.record_by_type);
      });
      setMachinesBySection(data.machines_by_section);
    })
    .catch(() => {})
    .finally(() => { window.dispatchEvent(new CustomEvent('frank:own-machines-changed')); })
);
